import { useState } from 'react';
import { Badge } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import { Icon } from '@clay-run/glaze/icon';
import { InputField } from '@clay-run/glaze/input-field';
import { Text } from '@clay-run/glaze/text';
import type { GongFlowStepDetailWire } from '@shared/flow/gong-flow-catalog';
import type { StepDraft } from '@shared/flow/enrollment';

import { AUTOFILL_IGNORE_PROPS } from '../../../../lib/autofill';
import { RichHtmlField } from '../../../ui/RichHtmlField';
import { RichTextBodyField } from '../../../ui/RichTextBodyField';
import { FLOW_ICONS } from '../../icons';
import { BATCH_LABELS } from '../../EnrollQueue/BulkStepDefaultsEditor';
import {
  DEFAULT_LABELS,
  type StepOverrideEditorLabels,
  type StepOverrideSnapshot,
} from '../../EnrollQueue/StepOverrideEditor';
import type { StagedStepCopy } from '../../EnrollQueue/state/staged-play-config';
import { useStepOverrideRowModel } from '../../EnrollQueue/useStepOverrideRowModel';

/**
 * The V2 rail's per-step override row (plan 2026-09-30-002, U3 step 3;
 * copy locked by D29 / plan-007 §17). ONE row component for BOTH scopes —
 * a lead's own steps inside `LeadExpandedBody`, and the batch defaults
 * inside `BulkEditor` — because D6 is "one step editor at two scopes, never
 * two independent editors". Scope changes exactly two things: which label
 * constant supplies the hints (`BATCH_LABELS` vs `DEFAULT_LABELS`) and
 * where the caller's draft callbacks point. Nothing about lead COUNT
 * reaches this file.
 *
 * It is the VIEW half only. Every draft/SSE defence lives in
 * `EnrollQueue/useStepOverrideRowModel` — the same hook V1's
 * `StepOverrideRow` consumes, so the two renderers cannot drift on the
 * derivation ladder, the override test, or the body seed. Read that hook's
 * header before changing anything here; three things it owns are
 * load-bearing and are the reason **this file contains no effects at all**
 * (enforced by U3's `grep` gate over this path):
 *
 *  1. `effective` is derived at render time (`localDraft ?? persistedOverride
 *     ?? batchDraft ?? stepDefault`) with no reseed effect. The draft store
 *     is never reseeded from server data, so the derived value IS the
 *     re-render-proof pattern here (`docs/v4-system/draft-state-under-sse.md`).
 *  2. The render-phase `lastKnownBodyRef` compare-and-set is what tells an
 *     EXTERNAL write to the body apart from this row's own typing.
 *  3. `bodySeedKey` bumps only on an external change or an explicit
 *     `Use flow default`; own typing must never bump it, or the caret resets
 *     mid-keystroke.
 *
 * Every v4-tree editing surface re-renders on any `artifact:updated` SSE
 * event, so a plain `useState` body here would drop keystrokes — hence
 * `RichHtmlField`/`RichTextBodyField` (a plain textarea would corrupt Gong's
 * HTML, and an inline `contentEditable` + HTML injection is banned by
 * `terra/no-contenteditable-with-inner-html`).
 *
 * Landmines this row encodes, all from real Gong failures:
 *  - A Subject field exists ONLY when `stepHasSubjectField(step)` (an email
 *    step that is not a reply). Gong rejects a subject on a reply step and
 *    400s the ENTIRE enrollment, not just that step — so the field is not
 *    rendered rather than shown-and-rejected.
 *  - An override is the PRESENCE of an own value, never a diff against the
 *    default: text identical to the flow default still shows `OVERRIDDEN`
 *    and still stops following the batch rung until reset.
 *  - There are no read-only steps. Every step type renders an editable body;
 *    blank is blank, and blank is exactly what makes Gong run its own script
 *    (PR #1636). No "Write your own" take-over, no italic placeholder line.
 */

export interface StepRowProps {
  /**
   * Which rung this row edits. `'lead'` → this lead's own override
   * (`DEFAULT_LABELS`); `'batch'` → the play group's step defaults every
   * clean lead follows (`BATCH_LABELS`, which also adds the
   * ` (all leads in this batch)` parenthetical to the expander's accessible
   * name — a multi-lead Gong section mounts this row at both scopes at once,
   * so without it the accessibility tree carries several buttons named
   * `Expand step 1 — Manual Email` with nothing to tell them apart).
   */
  scope: 'lead' | 'batch';
  /** Plain id, NOT an `EnrollmentItem` — only a `seedKey` ingredient. Batch scope passes the caller's own pseudo-id (`-1`). */
  itemId: number;
  /** The one live flow step this row renders (`GET /api/flow/gong/flows/:flowId/steps`). */
  step: GongFlowStepDetailWire;
  /** This scope's persisted override for THIS step, or `undefined` when it has none. Always `undefined` at batch scope (the batch rung is staged client-side, never persisted per step). */
  override: StepDraft | undefined;
  /** The caller's unsaved entry for one step (`values: null` = reset intent), or `null`. Must be referentially stable while unchanged (`useSyncExternalStore`). */
  localDraftFor: (stepNumber: number) => { values: StepOverrideSnapshot | null } | null;
  /** Send-time save failure for one step, or `null`. Renders as `Failed to save — retry`. Same stability requirement. */
  saveErrorFor: (stepNumber: number) => string | null;
  /** Subscribe half of the pair the row's two reads share; one subscription covers both. */
  subscribeStep: (stepNumber: number, onChange: () => void) => () => void;
  /**
   * The batch rung, keyed `String(step_order)` — a plain prop READ, never a
   * second subscription. Lead scope only, and only for a bulk-follow-eligible
   * lead (D24: a frozen `sending`/`queued`/`submitted` row must never start
   * following a batch default mid-send, so its caller omits this).
   */
  batchStepCopy?: Readonly<Record<string, StagedStepCopy>>;
  /** Fires on every settled keystroke with the FULL effective snapshot; the caller stores it locally (no network — Send is the one write moment). */
  onEditStepOverride: (stepNumber: number, stepType: string, values: StepOverrideSnapshot) => void;
  /** `Use flow default` — local intent until Send. */
  onResetStepOverride: (stepNumber: number, stepType: string) => void;
  /** `readOnly || frozen || busy` — every control in the row goes inert. */
  disabled: boolean;
  /** `'rich'` mounts the formatting toolbar; `'plain'` (default) the bare HTML field. Read off the play type's `ui.body_format`, never hardcoded per play slug. */
  bodyFormat?: 'plain' | 'rich';
  /** E17 — a 1-step flow renders its fields inline with no collapsed header at all (`steps.length > 1` at the call site). */
  showHeader: boolean;
}

export function StepRow({
  scope,
  itemId,
  step,
  override,
  localDraftFor,
  saveErrorFor,
  subscribeStep,
  batchStepCopy,
  onEditStepOverride,
  onResetStepOverride,
  disabled,
  bodyFormat = 'plain',
  showHeader,
}: StepRowProps) {
  const {
    effective,
    isOverridden,
    hasSubject,
    isEmailBody,
    stepTypeLabel,
    subjectPreview,
    saveError,
    bodySeedKey,
    commit,
    reset,
  } = useStepOverrideRowModel({
    itemId,
    step,
    override,
    localDraftFor,
    saveErrorFor,
    subscribeStep,
    batchStepCopy,
    // The hook keeps V1's prop spellings; this row's own props use the
    // plan's `onEditStepOverride`/`onResetStepOverride` names (the shape
    // `StepRows`/`BulkEditor` pass down). One adapter, here.
    onChangeOverride: onEditStepOverride,
    onResetOverride: onResetStepOverride,
  });

  // Tier 1 draft-state-under-SSE: pure UI state, never seeded from a query,
  // so a plain `useState` is correct. A 1-step flow has no header to toggle,
  // so its fields start visible.
  const [open, setOpen] = useState(!showHeader);

  // Annotated rather than inferred: `DEFAULT_LABELS` is an `as const` object
  // with no `stepScope` key at all (lead scope renders no parenthetical),
  // so the inferred union has no `stepScope` to read.
  const labels: StepOverrideEditorLabels = scope === 'batch' ? BATCH_LABELS : DEFAULT_LABELS;
  const bodyLabel = isEmailBody ? 'Body' : 'Notes / script';
  // `subjectHint` carries its own em dash (`— this lead only`); `bodyHint` is
  // the TAIL only, because the `HTML override, ` prefix is a fact about the
  // FIELD, not about lead count, and must survive a batch-scope override.
  const subjectHint = labels.subjectHint ?? DEFAULT_LABELS.subjectHint;
  const bodyHint = `— ${isEmailBody ? 'HTML override, ' : ''}${labels.bodyHint ?? DEFAULT_LABELS.bodyHint}`;

  // Identical props for either body editor — only `bodyFormat` picks the
  // component, so the two branches cannot drift.
  const bodyFieldProps = {
    value: effective.body_draft,
    seedKey: bodySeedKey,
    disabled,
    onChange: (html: string) => {
      commit({ body_draft: html });
    },
    className:
      'mt-1 min-h-[90px] w-full rounded-md border-[0.5px] border-border-primary bg-bg-primary px-2 py-1 text-xs text-content-primary',
    // A contentEditable div is not labelable, so the field carries its own
    // accessible name rather than relying on a wrapping <label>.
    'aria-label': `${bodyLabel} for step ${step.step_order}`,
  };

  const fields = (
    <div className={showHeader ? 'grid gap-1.5 px-2 pb-2' : 'grid gap-1.5'}>
      {hasSubject && (
        <InputField
          label={
            <>
              Subject <span className="font-normal text-content-tertiary">{subjectHint}</span>
            </>
          }
          size="sm"
          aria-label={`Subject for step ${step.step_order}`}
          value={effective.subject_draft}
          disabled={disabled}
          onValueChange={(value: string) => {
            commit({ subject_draft: value });
          }}
          {...AUTOFILL_IGNORE_PROPS}
        />
      )}
      <div className="text-xs text-content-secondary">
        {bodyLabel} <span className="font-normal text-content-tertiary">{bodyHint}</span>
        {bodyFormat === 'rich' ? <RichTextBodyField {...bodyFieldProps} /> : <RichHtmlField {...bodyFieldProps} />}
      </div>
      <div className="mt-0.5 flex items-center justify-between">
        {/* Error-only feedback (no background saves exist): a failed
            send-time save is the one thing this slot ever says. */}
        {saveError ? (
          <Text size="xs" variant="critical" role="alert">
            Failed to save — retry
          </Text>
        ) : (
          <span />
        )}
        {isOverridden && (
          <Button variant="ghost" size="xs" disabled={disabled} onClick={reset}>
            Use flow default
          </Button>
        )}
      </div>
    </div>
  );

  if (!showHeader) return fields;

  return (
    <div className="mb-1.5 rounded-md border-[0.5px] border-border-primary">
      {/* FIX 2 (rail-glaze-parity QA round) — the whole header toggles `open`
          now, not just the trailing chevron (ported from the prototype's
          `StepRow.tsx`, which wraps the entire header in one native
          `<button>`). The step number/label span and the OVERRIDDEN badge
          are plain, non-interactive markup, so nesting them inside the
          button is safe; the chevron itself drops from an `IconButton` to a
          decorative `Icon` since it's no longer independently clickable. */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} step ${step.step_order} — ${stepTypeLabel}${labels.stepScope ? ` (${labels.stepScope})` : ''}`}
        className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs"
      >
        <span className="font-medium tabular-nums text-content-tertiary">{step.step_order}</span>
        <span className="text-content-primary">
          {stepTypeLabel}
          {subjectPreview && <span className="font-normal text-content-tertiary"> — {subjectPreview}</span>}
        </span>
        {isOverridden && (
          <Badge color="blue" size="xs" className="uppercase tracking-wide">
            OVERRIDDEN
          </Badge>
        )}
        <Icon
          type={open ? FLOW_ICONS.expanded : FLOW_ICONS.expand}
          size="xs"
          className="ml-auto text-content-tertiary"
          aria-hidden
        />
      </button>
      {open && fields}
    </div>
  );
}
